import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Divider, Paper, Stack, Typography, useTheme } from '@mui/material';
import { CheckCircle, ErrorOutline } from '@mui/icons-material';
import financeService from '../../../services/financeService';
import { LedgerControls } from '../../../types/finance';
import { useCurrencyFormat } from '../../../hooks/useCurrencyFormat';
import { useTimezone } from '../../../hooks/useTimezone';

type Tone = 'success' | 'warning' | 'error';

/** One control: what it compares, whether the two agree, and both figures. */
const ControlRow: React.FC<{ title: string; status: string; tone: Tone; figures: Array<[string, number]>; note?: React.ReactNode }> = ({
  title, status, tone, figures, note,
}) => {
  const theme = useTheme();
  const money = useCurrencyFormat();
  return (
    <Box sx={{ px: 2.5, py: 1.75 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} divider={<Divider orientation="vertical" flexItem />} spacing={{ xs: 1.5, md: 0 }}>
        <Box sx={{ flex: 1.2, pr: { md: 2.5 } }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
            {title}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5, color: theme.palette[tone].dark }}>
            {tone === 'success' ? <CheckCircle fontSize="small" /> : <ErrorOutline fontSize="small" />}
            <Typography variant="body2" fontWeight={600}>{status}</Typography>
          </Stack>
        </Box>
        {figures.map(([label, paisa]) => (
          <Box key={label} sx={{ flex: 1, px: { md: 2.5 } }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="subtitle1" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(paisa / 100)}</Typography>
          </Box>
        ))}
      </Stack>
      {note && <Box sx={{ mt: 1 }}>{note}</Box>}
    </Box>
  );
};

const agreement = (differencePaisa: number, money: (n: number) => string): [string, Tone] => (differencePaisa
  ? [`Differs by ${money(Math.abs(differencePaisa) / 100)}`, 'error']
  : ['Ledger agrees', 'success']);

/** The ledger's controls: fees receivable, advance fees and fines receivable against their sub-ledgers. */
const LedgerControlsCard: React.FC = () => {
  const money = useCurrencyFormat();
  const { formatDateTime } = useTimezone();
  const [controls, setControls] = useState<LedgerControls | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setControls(await financeService.getControls());
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not run the ledger controls.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  if (!controls) return null;
  const { fees, fines } = controls;
  const [feeStatus, feeTone] = agreement(fees.differencePaisa, money);
  const [fineStatus, fineTone] = agreement(fines.differencePaisa, money);
  const lastRuns = [fees.lastRun, fines.lastRun].filter(Boolean) as Array<{ ranAt: string; differencePaisa: number }>;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2, overflow: 'hidden' }}>
      <ControlRow
        title="Fees receivable control"
        status={feeStatus}
        tone={feeTone}
        figures={[['Outstanding on challans', fees.subLedgerPaisa], ['Fees receivable (1130)', fees.ledgerPaisa]]}
        note={(fees.advances.subLedgerPaisa !== 0 || fees.advances.ledgerPaisa !== 0) && (
          <Stack direction="row" spacing={1} alignItems="center">
            {fees.advances.differencePaisa === 0
              ? <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
              : <ErrorOutline sx={{ fontSize: 16, color: 'error.main' }} />}
            <Typography variant="body2" color="text.secondary">
              Advance fees (2210): families hold <strong>{money(fees.advances.subLedgerPaisa / 100)}</strong>
              {fees.advances.differencePaisa === 0 ? ' — agrees with the ledger.' : `; the ledger shows ${money(fees.advances.ledgerPaisa / 100)}.`}
            </Typography>
          </Stack>
        )}
      />
      {(fines.subLedgerPaisa !== 0 || fines.ledgerPaisa !== 0) && (
        <>
          <Divider />
          <ControlRow
            title="Fines receivable control"
            status={fineStatus}
            tone={fineTone}
            figures={[['Owed on fine letters', fines.subLedgerPaisa], ['Fines receivable (1135)', fines.ledgerPaisa]]}
          />
        </>
      )}
      {lastRuns.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2.5, py: 0.75, borderTop: 1, borderColor: 'divider' }}>
          Last nightly check {formatDateTime(lastRuns[0].ranAt)} — {lastRuns.every(r => r.differencePaisa === 0) ? 'agreed' : 'a control differed'}
        </Typography>
      )}
    </Paper>
  );
};

export default LedgerControlsCard;
